import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip);

const API = process.env.REACT_APP_API;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const chartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 10 } },
    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
  },
};

function Tile({ label, value, accent }) {
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-xl">
      <div className={`text-3xl font-bold ${accent || "text-primary"}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function daySeries(rows, color) {
  return {
    labels: (rows || []).map((r) => r.day.slice(5)),
    datasets: [
      {
        data: (rows || []).map((r) => r.count),
        backgroundColor: color,
        borderColor: color,
        borderRadius: 4,
        maxBarThickness: 22,
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };
}

export default function Insights() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/metrics/overview`, { headers: authHeaders() })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.status === 403 ? "forbidden" : "Could not load metrics."));
  }, []);

  // Not the owner: no explanation, no trace — just the dashboard.
  if (error === "forbidden") return <Navigate to="/refer/dashboard" replace />;
  if (error) return <div className="p-8 text-center text-gray-500">{error}</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">Loading insights…</div>;

  const t = data.tiles;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
        <h2 className="text-2xl font-bold text-primary">Insights</h2>
        <span className="text-xs text-gray-400">as of {data.as_of.slice(0, 16).replace("T", " ")} UTC</span>
      </div>

      {/* Growth + revenue tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <Tile label="Total accounts" value={t.total_users} />
        <Tile label="New this week" value={`+${t.new_users_7d}`} accent="text-emerald-600" />
        <Tile label="Active trials" value={t.trials_active} />
        <Tile label="Paying" value={t.paying} accent="text-emerald-600" />
        <Tile label="MRR" value={`$${t.mrr}`} accent="text-emerald-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Tile label="Comped (codes/founders)" value={t.comped} accent="text-gray-700" />
        <Tile label="Expired / churned" value={t.expired_or_churned} accent="text-amber-600" />
        <Tile label="Codes redeemed" value={t.codes_redeemed} accent="text-gray-700" />
        <Tile label="Referrers logged (all users)" value={t.referrer_links_total} accent="text-gray-700" />
        <Tile label="Referrals logged (all users)" value={t.referral_events_total} accent="text-gray-700" />
      </div>

      {/* Time series */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 bg-white border border-gray-200 rounded-xl">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Signups — last 30 days</h3>
          <div className="h-44">
            <Bar data={daySeries(data.signups_by_day, "#2a78d6")} options={chartOpts} />
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-xl">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily active users</h3>
          <div className="h-44">
            <Line data={daySeries(data.active_users_by_day, "#1baf7a")} options={chartOpts} />
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-xl">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Referrals logged / day</h3>
          <div className="h-44">
            <Bar data={daySeries(data.events_by_day, "#eb6834")} options={chartOpts} />
          </div>
        </div>
      </div>

      {/* Recent signups */}
      <div className="p-4 bg-white border border-gray-200 rounded-xl">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Recent signups</h3>
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase">
            <tr><th className="py-1 pr-4">When</th><th className="py-1 pr-4">Name</th><th className="py-1">Email</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.recent_signups.map((u) => (
              <tr key={u.email}>
                <td className="py-1.5 pr-4 text-gray-500">{(u.created_at || "").slice(0, 10)}</td>
                <td className="py-1.5 pr-4">{u.name || "—"}</td>
                <td className="py-1.5 text-gray-600">{u.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
